import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  TablePagination,
  Toolbar,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  CloudQueue as CloudIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { AdminLayout } from '../components/AdminLayout';
import apiClient from '../utils/api';

interface FileRecord {
  message_id: string;
  room_id: string;
  room_title: string;
  sender_name: string;
  sender_type: string;
  file_url: string;
  file_name: string;
  file_size: number | null;
  file_type: string | null;
  created_at: string;
  storage_type: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const FileManagement: React.FC = () => {
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1, limit: 50, total: 0, totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [storageFilter, setStorageFilter] = useState<string>('all');

  const fetchFiles = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.getFiles({
        page,
        limit: pagination.limit,
        search: search || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      if (response.success && response.data) {
        let filteredFiles = response.data.files;
        if (storageFilter !== 'all') {
          filteredFiles = filteredFiles.filter(
            (f: FileRecord) => f.storage_type === (storageFilter === 's3' ? 'S3' : 'ローカル')
          );
        }
        setFiles(filteredFiles);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      setError('ファイル一覧の取得に失敗しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, dateFrom, dateTo, storageFilter, pagination.limit]);

  useEffect(() => {
    fetchFiles(1);
  }, [fetchFiles]);

  const handlePageChange = (_: unknown, newPage: number) => {
    fetchFiles(newPage + 1);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination(prev => ({ ...prev, limit: parseInt(event.target.value) }));
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleString('ja-JP', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImageFile = (fileName: string): boolean => {
    return /\.(png|jpe?g|gif|webp)$/i.test(fileName);
  };

  const getDownloadUrl = (file: FileRecord): string => {
    const token = localStorage.getItem('adminToken') || '';
    if (file.file_url.startsWith('s3:')) {
      return `/api/files/${file.message_id}/download?token=${encodeURIComponent(token)}`;
    }
    return `${file.file_url}?token=${token}`;
  };

  const senderTypeLabel = (type: string): string => {
    switch (type) {
      case 'admin': return 'オペレーター';
      case 'end_user': return 'お客様';
      default: return type;
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={700}>
            ファイル管理
          </Typography>
          <Tooltip title="更新">
            <IconButton onClick={() => fetchFiles(pagination.page)}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* フィルター */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Toolbar disableGutters sx={{ gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="ファイル名・ルーム名で検索"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchFiles(1)}
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              type="date"
              label="開始日"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              type="date"
              label="終了日"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>保存先</InputLabel>
              <Select
                value={storageFilter}
                label="保存先"
                onChange={(e) => setStorageFilter(e.target.value)}
              >
                <MenuItem value="all">すべて</MenuItem>
                <MenuItem value="s3">S3</MenuItem>
                <MenuItem value="local">ローカル</MenuItem>
              </Select>
            </FormControl>
          </Toolbar>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component={Paper}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : files.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">ファイルが見つかりません</Typography>
            </Box>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 40 }}></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>ファイル名</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>ルーム</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>送信者</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>サイズ</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>保存先</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>アップロード日時</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 60 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.map((file) => (
                    <TableRow key={file.message_id} hover>
                      <TableCell>
                        {isImageFile(file.file_name || '') ? (
                          <ImageIcon fontSize="small" color="primary" />
                        ) : (
                          <FileIcon fontSize="small" color="action" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                          {file.file_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {file.room_title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2">{file.sender_name}</Typography>
                          <Chip
                            label={senderTypeLabel(file.sender_type)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatFileSize(file.file_size)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={file.storage_type === 'S3' ? <CloudIcon /> : <StorageIcon />}
                          label={file.storage_type}
                          size="small"
                          color={file.storage_type === 'S3' ? 'primary' : 'default'}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(file.created_at)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="ダウンロード">
                          <IconButton
                            size="small"
                            component="a"
                            href={getDownloadUrl(file)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handlePageChange}
                rowsPerPage={pagination.limit}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage="表示件数"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}件`}
              />
            </>
          )}
        </TableContainer>
      </Box>
    </AdminLayout>
  );
};

export default FileManagement;
